import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { Profession, Interest } from "../constants/userPreferences";
import { CookieService } from "../services/CookieService";

export interface Permission {
  id: string;
  permission_key: string;
  description: string;
}
export interface Role {
  id: number;
  name: string;
}
export interface User {
  id: string;
  email: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  streetaddress?: string;
  streetaddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  age?: number;
  phone?: string;
  companyName?: string;
  companyType?: string;
  title?: string;
  profession?: Profession[];
  professionOther?: string;
  interests?: Interest[];
  interestsOther?: string;
  permissions?: Permission[];
  roles?: Role[];
  preferences?: {
    theme?: "light" | "dark";
    notifications?: boolean;
    language?: string;
  };
  lastLoginAt?: string;
  mndaStatus?: string;
  entityid?: string;
  activeind?: boolean;
  cidn?: number;
  messagecount?: number;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  updateUserPermissions: (permissions: Permission[]) => void;
  updateUserRoles: (roles: Role[]) => void;
  updateUserPreferences: (preferences: User["preferences"]) => void;
  isAuthenticated: boolean;
  hasPermission: (permissionKey: string) => boolean;
  hasRole: (role: string) => boolean;
  bImpersonating: boolean;
  setImpersonating: (isImpersonating: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    return CookieService.getUserData();
  });
  const [bImpersonating, setImpersonating] = useState<boolean>(() => {
    return CookieService.getImpersonating();
  });

  const setUserWithLogging = useCallback((newUser: User | null) => {
    if (newUser) {
      const userWithTimestamp = {
        ...newUser,
        lastLoginAt: new Date().toISOString(),
        preferences: newUser.preferences || {
          theme: "light",
          notifications: true,
          language: "en",
        },
      };
      setUser(userWithTimestamp);
      CookieService.setUserData(userWithTimestamp);
    } else {
      setUser(null);
      CookieService.removeUserData();
    }
  }, []);

  const setImpersonatingWithStorage = useCallback(
    (isImpersonating: boolean) => {
      setImpersonating(isImpersonating);
      CookieService.setImpersonating(isImpersonating);
    },
    [],
  );

  const updateUserPermissions = useCallback((permissions: Permission[]) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      const updatedUser = {
        ...prevUser,
        permissions,
      };
      CookieService.setUserData(updatedUser);
      return updatedUser;
    });
  }, []);

  const updateUserRoles = useCallback((roles: Role[]) => {
    setUser((prevUser) => {
      if (!prevUser) return null;
      const updatedUser = {
        ...prevUser,
        roles,
      };
      CookieService.setUserData(updatedUser);
      return updatedUser;
    });
  }, []);

  const updateUserPreferences = useCallback(
    (preferences: User["preferences"]) => {
      setUser((prevUser) => {
        if (!prevUser) return null;
        const updatedUser = {
          ...prevUser,
          preferences: {
            ...prevUser.preferences,
            ...preferences,
          },
        };
        CookieService.setUserData(updatedUser);
        return updatedUser;
      });
    },
    [],
  );

  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      if (!user || !Array.isArray(user.permissions) || !permissionKey) {
        return false;
      }

      const normalizedKey = permissionKey.toLowerCase();
      return user.permissions.some(
        (permission) =>
          permission?.permission_key?.toLowerCase() === normalizedKey,
      );
    },
    [user],
  );

  const hasRole = useCallback(
    (roleName: string): boolean => {
      if (!user || !Array.isArray(user.roles)) return false;
      return user.roles.some(
        (role: Role) => role.name.toLowerCase() === roleName.toLowerCase(),
      );
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      setUser: setUserWithLogging,
      updateUserPermissions,
      updateUserRoles,
      updateUserPreferences,
      isAuthenticated: !!user,
      hasPermission,
      hasRole,
      bImpersonating,
      setImpersonating: setImpersonatingWithStorage,
    }),
    [
      user,
      setUserWithLogging,
      updateUserPermissions,
      updateUserRoles,
      updateUserPreferences,
      hasPermission,
      hasRole,
      bImpersonating,
      setImpersonatingWithStorage,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
